import type { FilterFacet } from './Filter.types';

/** Longest "Blue, Black"-style label a pill will show before it starts eliding names. */
export const MAX_SELECTION_LABEL_CHARS = 20;

export type FacetSelectionSummary = Readonly<{
  /** What the pill should read, tail included. */
  label: string;
  /** `names` = the label lists the selection; `none` = nothing selected. */
  mode: 'none' | 'names';
}>;

/** Default tail ("+2"), for the server and for tests; the pill passes a `tq` version. */
const defaultFormatMore = (omitted: number) => `+${omitted}`;

/**
 * Makes an active pill say WHAT is selected ("Blue, Black", "Magnet") rather than how many
 * (Jeff, 2026-10-08). No "Group:" prefix (Jeff, 2026-10-29: "instead of 'Style: Bold' it
 * should just say 'Bold'"); the pill puts the group back in its accessible name.
 *
 * When the names run past the pill it keeps the ones that fit and counts the rest —
 * "Art Deco, Beach, +3". It used to swap to the group name plus a count badge,
 * which changed the pill's whole visual style the moment a fourth option was ticked
 * (Jeff, 2026-10-23: "the pill shouldn't change visual style the 4th item"). At least one
 * name always survives, so the pill never degrades to a bare number. Pure, for testability.
 */
export const summarizeFacetSelection = (
  facet: Pick<FilterFacet, 'label' | 'options'>,
  activeFilters: ReadonlySet<string>,
  formatMore: (omitted: number) => string = defaultFormatMore,
): FacetSelectionSummary => {
  const selected = facet.options.filter(option => activeFilters.has(option.compositeCode));
  if (selected.length === 0) return { label: facet.label, mode: 'none' };

  const names = selected.map(option => option.label);
  const summary = (label: string): FacetSelectionSummary => ({ label, mode: 'names' });

  const full = names.join(', ');
  // One name has nothing to elide: an over-long single option is shown in full rather than
  // padded with a "+0" tail.
  if (full.length <= MAX_SELECTION_LABEL_CHARS || names.length === 1) return summary(full);

  // Drop names from the end until the label plus its "+N" tail fits. One name is the
  // floor: a pill reading only "+5" would say less than the count badge did.
  for (let shown = names.length - 1; shown > 1; shown--) {
    const label = `${names.slice(0, shown).join(', ')}, ${formatMore(names.length - shown)}`;
    if (label.length <= MAX_SELECTION_LABEL_CHARS) return summary(label);
  }
  return summary(`${names[0]}, ${formatMore(names.length - 1)}`);
};
